import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type CondicionesCargo = components['schemas']['CondicionesCargoRead']
export type CondicionCargo = components['schemas']['CondicionRead']
export type CondicionCodigo = components['schemas']['CondicionCodigo']
export type CondicionAlcance = components['schemas']['AlcanceCondicionesRead']
export type CondicionTanda = components['schemas']['TandaRead']
export type SolicitudCatalogo = components['schemas']['SolicitudCatalogoRead']

function base(businessId: string | undefined, positionId: string | undefined) {
  return `/businesses/${businessId}/sgsst/positions/${positionId}/condiciones`
}

function condicionesKey(businessId: string | undefined, positionId: string | undefined) {
  return ['condiciones-cargo', businessId, positionId]
}

/** No se pagina: la lista de condiciones es cerrada y tiene diecisiete. */
export function useCondicionesCargo(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: condicionesKey(businessId, positionId),
    queryFn: async () => (await api.get<CondicionesCargo>(base(businessId, positionId))).data,
    enabled: !!businessId && !!positionId,
  })
}

/** La condición queda PROPUESTA; confirmarla es un acto de firmante. */
export function useAgregarCondicion(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {codigo: CondicionCodigo; textoLibre?: string}) =>
      (
        await api.post<CondicionCargo>(base(businessId, positionId), {
          codigo: input.codigo,
          texto_libre: input.textoLibre ?? null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
    },
  })
}

/** Mutación y no consulta: cada llamada materializa las condiciones marcadas y emite un
 * `alcance_token` nuevo con su propio reloj. */
export function useAlcanceCondiciones(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {confirmar: CondicionCodigo[]; confirmarIds: string[]; descartar: CondicionCodigo[]}) =>
      (
        await api.post<CondicionAlcance>(`${base(businessId, positionId)}/tandas/alcance`, {
          confirmar: input.confirmar,
          confirmar_ids: input.confirmarIds,
          descartar: input.descartar,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
    },
  })
}

/** Confirma y descarta el cargo entero en una sola constancia. */
export function useConfirmarTanda(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      condicionIds: string[]
      descartes: {condicion_id: string; motivo?: string}[]
      alcanceToken: string
    }) =>
      (
        await api.post<CondicionTanda>(`${base(businessId, positionId)}/tandas/confirmar`, {
          condicion_ids: input.condicionIds,
          descartes: input.descartes,
          alcance_token: input.alcanceToken,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
      track('condiciones_tanda_confirmada', {business_id: businessId, confirmadas: data.confirmadas})
    },
  })
}

/** Camino de a una para alcances triviales; el servidor vuelve a evaluar si lo son. */
export function useConfirmarCondicion(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (condicionId: string) =>
      (await api.post<CondicionCargo>(`${base(businessId, positionId)}/${condicionId}/confirmar`, {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
    },
  })
}

/** Motivo opcional (KAI-160); sobre una condición ya descartada solo corrige el motivo. */
export function useDescartarCondicion(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {condicionId: string; motivo?: string}) =>
      (
        await api.post<CondicionCargo>(`${base(businessId, positionId)}/${input.condicionId}/descartar`, {
          motivo: input.motivo || null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
    },
  })
}

export function usePedirCondicionAInci(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (condicionId: string) =>
      (await api.post<SolicitudCatalogo>(`${base(businessId, positionId)}/${condicionId}/pedir-a-inci`, {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: condicionesKey(businessId, positionId)})
    },
  })
}
